/**
 * Reconnecting WebSocket client speaking the protocol in shared/protocol.ts.
 */
import type { ClientMessage, ServerMessage } from '@clinspace/shared';
import { wsUrl } from './api.ts';

type Listener<T extends ServerMessage['type']> = (
  message: Extract<ServerMessage, { type: T }>,
) => void;

export class SocketClient {
  private socket: WebSocket | undefined;
  private listeners = new Map<string, Set<(m: ServerMessage) => void>>();
  private queue: ClientMessage[] = [];
  private retryMs = 1000;
  private reconnectTimer: number | undefined;
  private closed = false;
  sessionId: string | undefined;
  private statusListeners = new Set<(connected: boolean) => void>();
  /** True once the socket has opened at least once (a later close is a drop, not a start). */
  private everOpened = false;

  connect(): void {
    this.closed = false;
    this.open();
  }

  close(): void {
    this.closed = true;
    this.socket?.close();
  }

  get connected(): boolean {
    return this.socket?.readyState === WebSocket.OPEN;
  }

  send(message: ClientMessage): void {
    if (this.connected) this.socket!.send(JSON.stringify(message));
    else this.queue.push(message);
  }

  /** Connection drops and recoveries (after the first open). */
  onStatus(listener: (connected: boolean) => void): () => void {
    this.statusListeners.add(listener);
    return () => this.statusListeners.delete(listener);
  }

  on<T extends ServerMessage['type']>(type: T, listener: Listener<T>): () => void {
    if (!this.listeners.has(type)) this.listeners.set(type, new Set());
    const set = this.listeners.get(type)!;
    set.add(listener as (m: ServerMessage) => void);
    return () => set.delete(listener as (m: ServerMessage) => void);
  }

  private open(): void {
    const socket = new WebSocket(wsUrl());
    this.socket = socket;
    socket.addEventListener('open', () => {
      this.retryMs = 1000;
      if (this.everOpened) for (const l of this.statusListeners) l(true);
      this.everOpened = true;
      for (const m of this.queue.splice(0)) socket.send(JSON.stringify(m));
      this.emit({ type: 'pong', t: 0, serverTime: new Date().toISOString() });
    });
    socket.addEventListener('message', (event) => {
      let message: ServerMessage;
      try {
        message = JSON.parse(String(event.data)) as ServerMessage;
      } catch {
        return;
      }
      if (message.type === 'welcome') this.sessionId = message.sessionId;
      this.emit(message);
    });
    socket.addEventListener('close', () => {
      if (this.closed || this.reconnectTimer !== undefined) return;
      if (this.everOpened) for (const l of this.statusListeners) l(false);
      // One pending reconnect at a time, with exponential backoff up to 30 s.
      this.reconnectTimer = window.setTimeout(() => {
        this.reconnectTimer = undefined;
        this.open();
      }, this.retryMs);
      this.retryMs = Math.min(this.retryMs * 2, 30_000);
    });
    socket.addEventListener('error', () => socket.close());
  }

  private emit(message: ServerMessage): void {
    for (const l of this.listeners.get(message.type) ?? []) l(message);
  }
}
